import { Component, effect, ElementRef, inject, Input, signal, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { BinderFile, FileCategory } from 'src/app/models/binder-file';
import { DialogMode } from 'src/app/models/dialog-mode.model';
import { OverlayPanel } from 'primeng/overlaypanel';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { AddFileComponent } from 'src/app/landmark/binder/doc-viewer/add-file.component';
import { DocGeneratorService } from '@/services/api/doc-generator.service';
import { BinderDialogService } from '@/services/app/binder-dialog.service';
import { ShortcutHandlerService } from '@/services/app/shortcut-handler.service';
import { BinderService } from '@/services/api/binder.service';
import { MenubarModule } from 'primeng/menubar';
import { TimeAgoPipe } from "../../../pipes/time-ago.pipe";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PdfViewerComponent, PdfViewerModule } from 'ng2-pdf-viewer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { SettingsDialogService } from '@/services/app/settings-dialog.service';
import { CheckboxModule } from 'primeng/checkbox';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';

import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { UserAccessService } from '@/services/app/user-access.service';
import { DialogModule } from 'primeng/dialog';
import { FilesService } from '@/services/api/files.service';
import { DocStateService } from '@/services/api/doc-state.service';

@Component({
  selector: 'app-doc-viewer',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    OverlayPanelModule,
    ButtonModule,
    AddFileComponent,
    TimeAgoPipe,
    PdfViewerModule,
    MenubarModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    ButtonGroupModule,
    CheckboxModule,
    InputGroupModule,
    InputGroupAddonModule,
    ProgressSpinnerModule,
    DialogModule
  ],
  template: `
    <p-menubar [model]="items">
  <ng-template #end>
    <p-iconfield>
      <p-inputicon styleClass="pi pi-search" />
      <input type="text" pInputText placeholder="Search" (input)="onSearchInput($event)"
        (keydown)="onSearchKeydown($event)" (focus)="onSearchFocus()" (blur)="onSearchBlur()" #searchInput />
      <p-tag [value]="searchFocused ? 'Enter' : getShortcutPrefix('K')"
        styleClass="absolute right-0 top-1/2 -translate-y-1/2 mr-2" />
    </p-iconfield>
  </ng-template>
</p-menubar>
<p-overlayPanel #op>
  <div class="flex flex-column gap-3 w-25rem">
    <div>
      <span class="font-medium text-900 block mb-2">Hit enter to skip to the next result.</span>
    </div>
  </div>
</p-overlayPanel>
<div class="flex flex-col md:flex-row gap-8" style="height: 95%">
  <div class="md:w-[20rem] card !p-0">
    <div class="flex justify-content-between items-center mb-2">
      <p-checkbox [binary]="true" class="p-2 align-middle" size="large" *ngIf="!disableGeneration()" [(ngModel)]="allSelected" />
      <h2 class="text-2xl font-bold p-2">Files</h2>
    </div>
    <div class="border-round shadow-2 text-left" *ngFor="let category of categories"
      style="border-bottom: 1px solid var(--surface-border)">
      <div class="d-flex text-black" style="display: flex; width: 100%;">

        <!-- Square Icon Cells -->
        <div class="icon-cell" *ngIf="!disableGeneration()"
          style="width: 50px; height: 50px; flex-shrink: 0; display: flex; justify-content: center; align-items: center;">
          <div *ngIf="category.generating; else fileIcon">
            <p-progressSpinner styleClass="w-2rem h-2rem" strokeWidth="3" fill="var(--surface-ground)"
              animationDuration="1s" [style]="{ width: '36px', height: '36px' }"></p-progressSpinner>
          </div>
          <ng-template #fileIcon>
            <div class="action-item" *ngIf="category.interactType === 'generate'">
              <p-checkbox [binary]="true" class="align-middle" size="large" [(ngModel)]="category.categorySelected" />
            </div>
            <ng-template #nonLink>
              <p-button *ngIf="category.interactType !== 'uploadMany'" icon="pi pi-file" [disabled]="true"
                [rounded]="false" [text]="true" [raised]="true" severity="success"></p-button>
            </ng-template>
          </ng-template>
        </div>

        <!-- Middle Cell -->
        <div class="middle-cell" style="flex-grow: 1;">
          <p-button [outlined]="true" (click)="setSelection(category)"
            [style]="{'width': '100%', 'justify-content': 'flex-start', 'overflow': 'hidden'}"
            [disabled]="isSelected(category)" class="w-full !text-left" styleClass="justify-content-start"
            *ngIf="category.fileCategoryId !== 8; else other">
            <div>
              <div class="block text-900 font-medium mb-1" (click)="setSelection(category)">
                {{category.fileCategoryName}}</div>
              <div *ngIf="category.interactType !== 'uploadMany'" class="text-600 justify-content-start text-left ml-0"> 
                <span *ngIf="category.binderFiles; else noFiles">
                {{ getFileDateDisplay(getMostRecentFile(category)!) | timeAgo }}
                </span>
                <ng-template #noFiles>
                  <span class="text-600"> {{category.interactType === 'generate' ? 'Not Yet Generated' : 'No Files'}}</span>
                </ng-template>
                </div>
              <div *ngIf="category.fileCategoryId === 8">
                <div *ngFor="let file of otherFiles">
                  <div class="icon-cell"
                    style="width: 50px; height: 50px; flex-shrink: 0; display: flex; justify-content: start; align-items: center;">
                    <div class="action-item">
                      <p-button icon="pi pi-file-pdf" [rounded]="false" [text]="true" [raised]="true"
                        severity="success"></p-button>
                    </div>
                  </div>
                  <div class="block text-900 font-medium mb-1 justify-content-start text-left">{{file.fileName}}</div>
                  <div class="text-600 justify-content-start text-left ml-0">{{file.fileName}} - {{ (file?.modifiedOn !== null && file?.modifiedOn !==
                    undefined ?
                    file?.modifiedOn : file?.createdOn) | timeAgo }}
                  </div>
                </div>
              </div>
            </div>
          </p-button>

          <ng-template #other>
            <div style="display: flex; align-items: center; flex-grow: 1;">
              <div class="block text-900 font-medium mb-1" (click)="setSelection(category)">
                {{category.fileCategoryName}}</div>
              <div *ngIf="category.interactType !== 'uploadMany'" class="text-600">{{
                (category.binderFiles?.[0]?.modifiedOn ? category.binderFiles?.[0]?.modifiedOn :
                category.binderFiles?.[0]?.createdOn) | timeAgo }} <span
                *ngIf="category.outOfDate && category.interactType === 'generate'" class="text-900" style="color: red;"> -
                OUTDATED</span>
              </div>
              <p-buttongroup *ngFor="let file of otherFiles" class="w-full mb-4">
                <p-button variant="text" icon="pi pi-file-pdf" [disabled]="isSelected(category)" [raised]="true"
                  [label]="category.fileCategoryName" (click)="setSelection(category)"
                  [style]="{'width': '100%', 'justify-content': 'flex-start', 'overflow': 'hidden'}"
                  class="w-full text-left" styleClass="justify-content-start">
                </p-button>
                <p-button variant="text" [raised]="true" icon="pi pi-trash" />
              </p-buttongroup>
            </div>
          </ng-template>
        </div>
        <div class="action-buttons"
          style="flex-shrink: 1; display: flex; justify-content: flex-end; align-items: center;">
          <div class="action-item">
            <p-button
              *ngIf="showButton(category)"
              [icon]="category.interactType === 'uploadMany' || category.interactType === 'upload' ? 'pi pi-upload' : 'pi pi-cog'"
              [rounded]="false" [text]="true" severity="success" class="w-full h-full text-left"
              (click)="openSettings(category)" styleClass="justify-content-start"></p-button>
          </div>
        </div>
      </div>
    </div>
    <p-button icon="pi pi-sync" [raised]="true" [label]="'Generate Selected'" (click)="generateSelected()"
      [disabled]="disableGeneration()" [style]="{'width': '100%'}" class="w-full text-left"
      styleClass="justify-content-start" severity="info" />
  </div>
  <div class="flex-1 card !p-0">
    <pdf-viewer [src]="pdfSrc" #pdfViewerComponent [autoresize]="true" [fit-to-page]="true" [render-text]="true"
      [original-size]="false" class="pdf-viewer" [ngStyle]="{'height': '100%', 'width': '100%'}"
      (error)="onPdfError($event)" (after-load-complete)="onPdfLoadComplete()"></pdf-viewer>
    <div *ngIf="!pdfSrc" class="flex items-center justify-center h-full">
      <p class="text-surface-600">No document selected</p>
    </div>
  </div>
</div>
<p-dialog header="Upload Document" [modal]="true" [(visible)]="uploadVisible" [style]="{ width: '25rem' }">
  <app-add-files [fileCategory]="selectedCategory" (uploadComplete)="onUploadComplete()"></app-add-files>
</p-dialog>
    `,
  styles: `

`
})
export class DocViewerComponent {
  binderFiles: BinderFile[] = [];
  otherFiles: BinderFile[] = [];
  categories: FileCategory[] = [];
  uploadVisible: boolean = false;
  @Input() maximized: boolean = false;
  @Input() embeded: boolean = false;
  sanitizedPdfSrc: SafeResourceUrl = '';
  items = [
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      shortcut: '⌘+S',
      command: () => this.openSettingsDialog(),
    },
    {
      label: 'Download',
      icon: 'pi pi-download',
      command: () => this.downloadPdf(),
    }
  ];

  docGenerator = inject(DocGeneratorService);
  filesService = inject(FilesService);
  binderDialogService = inject(BinderDialogService);
  docStateService = inject(DocStateService);
  shortcutHandlerService = inject(ShortcutHandlerService);
  currentBinderService = inject(BinderService);
  sanitizer = inject(DomSanitizer);
  settingsDialogService = inject(SettingsDialogService);
  userAccessService = inject(UserAccessService);
  categoriesWithOptions = [1,4,5];

  dialogModeSubscription!: Subscription;
  searchFocused: boolean = false;
  dialogMode: DialogMode | null = null;
  selectedSettings: string | null= null;
  settingsVisible: boolean = false;

  @ViewChild(PdfViewerComponent) private pdfComponent!: PdfViewerComponent;
  @ViewChild('searchInput') searchInputElement!: ElementRef;
  @ViewChild('foverlay') foverlay!: OverlayPanel;

  pdfSrc = "https://lexarprojects.blob.core.windows.net/landmark/placeholder.pdf";
  searchQuery = '';
  selectedCategory: FileCategory | null = null;
  private pdfUpdateTimeout: any;
  private isPdfLoading = false;

  constructor(
  ) {
    effect(() => {
      const hiddenAccess = this.currentBinderService.currentBinder().enableExtraTemplates;
      const docData = this.filesService.categories().filter(category => category.archived !== true);
      if (docData) {
        if(hiddenAccess) {
          this.categories = docData.filter(category => category.archived !== true);
        } else {
          this.categories = this.filesService.categories().filter(category => category.archived !== true && category.hidden !== true);
        }
        // console.log('categories', this.categories);
      }
    });
    effect(() => {
      const category = this.filesService.viewedCategory();
      if (category) {
        const file = this.filesService.getMostRecentFile(category.fileCategoryId);
    
        if (file?.blobUrl) {
          const timestamp = new Date(file.modifiedOn || file.createdOn || new Date()).getTime();
          const fullUrl = `${file.blobUrl}?t=${timestamp}`;
    
          // Prevent reloading the same file
          if (this.pdfSrc !== fullUrl) {
            this.pdfSrc = fullUrl;
            this.sanitizedPdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
          }
        } else {
          this.pdfSrc = '';
          this.sanitizedPdfSrc = '';
        }
      }
    });
    
    
  }

  onPdfError(error: any) {
    console.warn('PDF viewer error:', error);
    // Handle PDF errors gracefully - don't show to user unless critical
  }

  onPdfLoadComplete() {
    this.isPdfLoading = false;
  }

  disableGeneration() {
    return !this.categories.filter(category => category.interactType === 'generate').some(category => category.categorySelected) || !this.userAccessService.binderEditAccess();
  }

  focusOnSearchInput() {
    this.searchInputElement.nativeElement.focus();
  }

  showButton(category: FileCategory) {
    return this.categoriesWithOptions.includes(category.fileCategoryId) || category.interactType === 'uploadMany' || category.interactType === 'upload';
  }

  get allSelected() {
    return this.categories.filter(category => category.interactType === 'generate').every(category => category.categorySelected);
  }

  set allSelected(value: boolean) {
    this.categories.filter(category => category.interactType === 'generate').forEach(category => category.categorySelected = value);
  }

  getSubtext(category: FileCategory) {
    if(category.interactType === 'generate') {
      return category.binderFiles?.[0]?.fileName;
    }
    return category.binderFiles?.[0]?.fileName;
  }

  onSearchInput(event: any) {
    const searchValue = event.target.value;
    this.searchQuery = searchValue;
    if (searchValue.trim()) {
      this.search(searchValue, 'find');
    }
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.searchQuery.trim()) {
        this.search(this.searchQuery, 'again');
      }
    }
  }

  onSearchFocus() {
    this.searchFocused = true;
  }

  onSearchBlur() {
    this.searchFocused = false;
  }

  getShortcutPrefix(letter: string) {
    return this.shortcutHandlerService.getShortcutPrefix(letter);
  }

  isSelected(category: FileCategory): boolean {
    const selectedCategory = this.filesService.viewedCategory();
    const file = this.getMostRecentFile(category);
    return selectedCategory?.fileCategoryId === category.fileCategoryId || !file?.blobUrl;
  }
  

  getMostRecentFile(category: FileCategory): BinderFile | undefined {
    return category.binderFiles?.slice().sort((a, b) =>
      this.getFileDate(b).getTime() - this.getFileDate(a).getTime()
    )[0];
  }
  
  getFileDate(file: BinderFile): Date {
    return new Date(file.modifiedOn ?? file.createdOn ?? 0);
  }
  
  getFileDateDisplay(file: BinderFile): Date | null {
    if(!file) return null;
    if (file.modifiedOn) return file.modifiedOn
    if (file.createdOn) return file.createdOn
    return null;
  }
  
  search(stringToSearch: string | null, type: 'find' | 'again') {
    if (!stringToSearch) return;
    // this.pdfComponent.eventBus.dispatch('find', {
    //   query: stringToSearch,
    //   type: 'again',
    //   caseSensitive: false,
    //   findPrevious: undefined,
    //   highlightAll: true,
    //   phraseSearch: true
    // });
    this.pdfComponent.eventBus.dispatch('find', {
      query: stringToSearch, type: type, caseSensitive: false, findPrevious: undefined, highlightAll: true, phraseSearch: true
    });
  }

  generateSelected() {
    this.docGenerator.generateSelected()
  }

  closeDialog() {
    this.binderDialogService.closeDialog();
  }

  onUploadComplete() {
    // this.docGenerator.loadFiles(this.currentBinderService.currentBinder().BinderID);
    this.foverlay.hide();
  }

  downloadPdf() {
    if (this.pdfSrc) {
      window.open(this.pdfSrc, '_blank');
    }
  }

  setSelection(event: FileCategory) {
    this.filesService.viewedCategory.set(event);
  }

  openSettings(setting: FileCategory) {
    if(setting.interactType === 'uploadMany' || setting.interactType === 'upload') {
      this.filesService.viewedCategory.set(setting);
      this.uploadVisible = true;
    }
    else {
      console.log('opening settings', setting);
      this.filesService.viewedCategory.set(setting);
      this.settingsDialogService.toggleDialog(setting.fileCategoryName+' Settings',setting.fileCategoryId,true);
      this.settingsVisible = true;
    }
  }

  openSettingsDialog() {
    const category = this.filesService.viewedCategory();
    if(category) {
      this.openSettings(category);
    }
  }

}
